import {useDebugValue, useEffect, useRef, useState} from 'react';

import classNames from 'classnames';

import {undef} from '../../../utils/undef';

const classNamespace = 'search-form';

const dayClassName = `${classNamespace}__day`;
const dayContentClassName = `${dayClassName}-content`;
const dayLabelClassName = `${dayClassName}-label`;
const dayValueClassName = `${dayClassName}-value`;

const selectDateHintDayClassName = `${classNamespace}__select-date-hint-day`;
const selectDateHintDayTooltipClassName = `${selectDateHintDayClassName}-tooltip`;
const selectDateHintDayTooltipFadeOutClassName = `${selectDateHintDayTooltipClassName}--fade-out`;
const selectDateHintDayTooltipWrapperClassName = `${selectDateHintDayTooltipClassName}-wrapper`;

const hiddenClassName = 'hidden';

const dateClassName = 'date';
const pastClassName = 'past';
const todayClassName = 'today';

function useComponent(props) {
    const {
        ariaLabelDate,
        dayData: {
            showSelectDateHint
        },
        day,
        isPast,
        isToday,
        selectDateHintMessage
    } = props;

    const [isShownSelectDateHint, setIsShownSelectDateHint] = useState(showSelectDateHint);
    const [isFadingOutSelectDateHint, setIsFadingOutSelectDateHint] = useState(false);

    const shownTimeoutId = useRef(null);
    const fadingTimeoutId = useRef(null);

    const className = classNames(dayClassName, selectDateHintDayClassName);

    const contentClassName = classNames(dayContentClassName);

    const labelClassName = classNames(dayLabelClassName, dateClassName, {
        [pastClassName]: isPast,
        [todayClassName]: isToday
    });

    const valueClassName = classNames(dayValueClassName, hiddenClassName);

    const tooltipPortalClassName = classNames(selectDateHintDayTooltipClassName, {
        [selectDateHintDayTooltipFadeOutClassName]: isFadingOutSelectDateHint
    });

    const tooltipWrapperClassName = classNames(selectDateHintDayTooltipWrapperClassName);

    const tooltipContent = selectDateHintMessage;
    const tooltipIsOpen = isShownSelectDateHint;
    const tooltipParentNode = typeof document !== undef && document.body;

    function handleTooltipClose() {
        clearTimeout(shownTimeoutId.current);
        shownTimeoutId.current = null;

        clearTimeout(fadingTimeoutId.current);
        fadingTimeoutId.current = null;

        setIsShownSelectDateHint(false);
        setIsFadingOutSelectDateHint(false);
    }

    function handleTooltipFadeOut() {
        clearTimeout(shownTimeoutId.current);
        shownTimeoutId.current = null;

        clearTimeout(fadingTimeoutId.current);

        setIsFadingOutSelectDateHint(true);

        fadingTimeoutId.current = setTimeout(() => {
            fadingTimeoutId.current = null;

            handleTooltipClose();
        }, 350);
    }

    function handleTooltipOpen() {
        shownTimeoutId.current = setTimeout(() => {
            shownTimeoutId.current = null;

            /* istanbul ignore else */
            if (isShownSelectDateHint) {
                handleTooltipFadeOut();
            }
        }, 2500);
    }

    useEffect(() => {
        if (!showSelectDateHint && isShownSelectDateHint) {
            handleTooltipFadeOut();
        }
    }, /* eslint-disable-line react-hooks/exhaustive-deps */ [showSelectDateHint, isShownSelectDateHint]); // FIXME: Include `handleTooltipFadeOut` in deps

    useEffect(() => () => {
        handleTooltipClose();
    }, []);

    const result = {
        className,
        contentClassName,
        labelClassName,
        valueClassName,
        tooltipPortalClassName,
        tooltipWrapperClassName,
        tooltipContent,
        tooltipIsOpen,
        tooltipParentNode,
        onTooltipClose: handleTooltipClose,
        onTooltipOpen: handleTooltipOpen,
        day,
        ariaLabelDate
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
